/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2021 ICEMEDX, Inc.
***********************************************/
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { tap, map } from 'rxjs/operators';
import { XmlparseService } from '../xml/xmlparse.service';

import { AlertService } from "../../services/alert/alert.service";
import { report } from "../../models/reports/report";
import { reportindex } from "../../models/reports/reportindex";
import { IceTypes } from "../../models/icetypes";
import { Iceutility } from "../../services/iceutility";
import { LoginService } from '../../login/login.service';
import { otheritems } from '../../models/otheritems/otheritems';
import { superbill } from '../../models/accounting/superbill';
import { assetjournal } from '../../models/accounting/assetjournal';
import { expensejournal } from '../../models/accounting/expensejournal';
import { incomerevenuejournal } from '../../models/accounting/incomerevenuejournal';
import { liabilityjournal } from '../../models/accounting/liabilityjournal';
import { otheritemsjournal } from '../../models/accounting/otheritemsjournal';
import { superbillcharges } from '../../models/accounting/medservice/superbillcharges';
import { orderobject } from '../../models/domain/order';
import { payment } from '../../models/accounting/payment';
import { medicalservice } from '../../models/accounting/medservice/medicalservice';
import { IcecontrolSettings } from '../../components/models/icesettings';
import { servicepaymentreference } from '../../models/accounting/servicepaymentreference';
import { accountingperiod } from '../../models/accounting/accountingperiod';
import { claimbucket } from '../../models/accounting/claim/claimbucket';
import { subscriberreference } from '../../models/ehr/subs/subscriberreference';
import { amount } from '../../models/ehr/subs/amount';
import { chargehistory } from '../../models/accounting/chargehistory';
import { entity } from '../../models/common/entity';

declare var require: any;

@Injectable()
export class AccountingService extends XmlparseService {

  constructor(private http: HttpClient, private alertSvc: AlertService, private loginSvc: LoginService) {
    super(alertSvc, loginSvc);
  }

  public getOtherItems(act: string, acttype: IceTypes.ACCOUNT_TYPE, keyword: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=otheritems&domaintype=" + acttype.toString() + "&key=&subkeys=" + "keyword:" + keyword + ";";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseOtherItemsIndex(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getOtherItem(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string, itemno?: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (key == null || key.length == 0) throw "Key input invalid";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=otheritems&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=" + (itemno != null ? "itemnumber:" + itemno : "");
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseOtherItems(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public addOtherItem(act: string, acttype: IceTypes.ACCOUNT_TYPE, item: otheritems): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (item == null) throw "Other Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=addotheritem&domaintype=" + acttype.toString() + "&key=" + item.other_items_key + "&subkeys=";

    let tblname = item.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(item) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseOtherItems(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public updateOtherItem(act: string, acttype: IceTypes.ACCOUNT_TYPE, item: otheritems): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (item == null) throw "Other Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=updateotheritem&domaintype=" + acttype.toString() + "&key=" + item.other_items_key + "&subkeys=";

    let tblname = item.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(item) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseOtherItems(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPurchases(act: string, acttype: IceTypes.ACCOUNT_TYPE, orderfrom: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=purchases&domaintype=" + acttype.toString() + "&key=&subkeys=" + "orderfrom:" + orderfrom + ";";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parsePurchasesIndex(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPurchaseDelivery(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=purchasedelivery&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parsePurchaseDelivery(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getItemReceiving(act: string, acttype: IceTypes.ACCOUNT_TYPE, itemkey: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=itemreceiving&domaintype=" + acttype.toString() + "&key=" + itemkey + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseItemReceiving(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public itemReceivingScan(act: string, acttype: IceTypes.ACCOUNT_TYPE, billkey: string, itemParams: any): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (itemParams == null) throw "Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=itemreceivingscan&domaintype=" + acttype.toString() + "&key=" + billkey + "&subkeys=";
    for (const param in itemParams) {
      url += param + ":" + itemParams[param] + ";";
    }

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseItemReceivingScan(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public itemReceivingScanItemQuantity(act: string, acttype: IceTypes.ACCOUNT_TYPE, billkey: string, itemParams: any): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (itemParams == null) throw "Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=itemreceivingscanquantity&domaintype=" + acttype.toString() + "&key=" + billkey + "&subkeys=";
    for (const param in itemParams) {
      url += param + ":" + itemParams[param] + ";";
    }

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseItemReceivingScan(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public itemReceivingScanItemCost(act: string, acttype: IceTypes.ACCOUNT_TYPE, billkey: string, itemParams: any): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (itemParams == null) throw "Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=itemreceivingscancost&domaintype=" + acttype.toString() + "&key=" + billkey + "&subkeys=";
    for (const param in itemParams) {
      url += param + ":" + itemParams[param] + ";";
    }

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseItemReceivingScan(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public itemReceivingScanItemRemove(act: string, acttype: IceTypes.ACCOUNT_TYPE, billkey: string, itemParams: any): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (itemParams == null) throw "Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=itemreceivingscanremove&domaintype=" + acttype.toString() + "&key=" + billkey + "&subkeys=";
    for (const param in itemParams) {
      url += param + ":" + itemParams[param] + ";";
    }

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseItemReceivingScan(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getBills(act: string, acttype: IceTypes.ACCOUNT_TYPE, billto: string,
    domType?: IceTypes.ACCOUNT_TYPE, fromDt?: string, toDt?: string, dateField?: string, status?: IceTypes.trans_status): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=bills&domaintype=" + acttype.toString() + "&key=&subkeys=" + "fordomain|" + billto + ";";
    if (domType != null) url += "fordomaintype|" + domType.toString() + ";";
    if (fromDt != null) url += "from|" + fromDt.toString() + ";";
    if (toDt != null) url += "to|" + toDt.toString() + ";";
    if (dateField != null) url += "datefield|" + dateField.toString() + ";";
    if (status != null) url += "status|" + status.toString() + ";";

    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseBillsIndex(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getBill(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=bill&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseBill(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getBillByService(act: string, acttype: IceTypes.ACCOUNT_TYPE, dom: string, domType: IceTypes.ACCOUNT_TYPE, key: string, includesvcs: boolean = false): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=billbyservice&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=" + "fordomain|" + dom + ";" + "fordomaintype|" + domType.toString() + ";";
    if (includesvcs) url += "includeservices|1;"
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseBill(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getService(act: string, acttype: IceTypes.ACCOUNT_TYPE, dom: string, domType: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=service&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=" + "fordomain|" + dom + ";" + "fordomaintype|" + domType.toString() + ";";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseBill(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getServices(act: string, acttype: IceTypes.ACCOUNT_TYPE, dom: string, domType: IceTypes.ACCOUNT_TYPE, fromDt: string, toDt: string, keywd: string, code: string, status: IceTypes.trans_status): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=services&domaintype=" + acttype.toString() + "&key=&subkeys=" + "fordomain|" + dom + ";" + "fordomaintype|" + domType.toString() + ";" + "from|" + fromDt + ";" + "to|" + toDt + ";" + "keyword|" + keywd + ";" + "code|" + code + ";" + "status|" + status + ";";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseServiceIndex(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public billServiceScan(act: string, acttype: IceTypes.ACCOUNT_TYPE, billkey: string, itemParams: any): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (itemParams == null) throw "Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=billservicescan&domaintype=" + acttype.toString() + "&key=" + billkey + "&subkeys=";
    for (const param in itemParams) {
      url += param + ":" + itemParams[param] + ";";
    }

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseBillServiceScan(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }
 
  public billServiceScanItemQuantity(act: string, acttype: IceTypes.ACCOUNT_TYPE, billkey: string, itemParams: any): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (itemParams == null) throw "Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=billservicescanquantity&domaintype=" + acttype.toString() + "&key=" + billkey + "&subkeys=";
    for (const param in itemParams) {
      url += param + ":" + itemParams[param] + ";";
    }

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseServiceScan(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public billServiceScanItemCode(act: string, acttype: IceTypes.ACCOUNT_TYPE, billkey: string, itemParams: any): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (itemParams == null) throw "Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=billservicescancode&domaintype=" + acttype.toString() + "&key=" + billkey + "&subkeys=";
    for (const param in itemParams) {
      url += param + "|" + itemParams[param] + ";";
    }

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseServiceScan(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public billServiceScanItemRemove(act: string, acttype: IceTypes.ACCOUNT_TYPE, billkey: string, itemParams: any): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (itemParams == null) throw "Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=billservicescanremove&domaintype=" + acttype.toString() + "&key=" + billkey + "&subkeys=";
    for (const param in itemParams) {
      url += param + ":" + itemParams[param] + ";";
    }

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseServiceScan(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public assignBilltoEncounter(act: string, acttype: IceTypes.ACCOUNT_TYPE, enckey: string, billkey: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=assignbilltoencounter&domaintype=" + acttype.toString() + "&key=" + enckey + "&subkeys=billkey:" + billkey;

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["ENCOUNTER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public unAssignBilltoEncounter(act: string, acttype: IceTypes.ACCOUNT_TYPE, enckey: string, billkey: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=unassignbilltoencounter&domaintype=" + acttype.toString() + "&key=" + enckey + "&subkeys=billkey:" + billkey;

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["ENCOUNTER"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public diagnosisServiceScanItemRemove(act: string, acttype: IceTypes.ACCOUNT_TYPE, billkey: string, itemParams: any): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (itemParams == null) throw "Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=diagnosisservicescanitemremove&domaintype=" + acttype.toString() + "&key=" + billkey + "&subkeys=";
    for (const param in itemParams) {
      url += param + ":" + itemParams[param] + ";";
    }

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseServiceScan(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public billDiagnosisScan(act: string, acttype: IceTypes.ACCOUNT_TYPE, billkey: string, itemParams: any): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (itemParams == null) throw "Item object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=billdiagnosisscan&domaintype=" + acttype.toString() + "&key=" + billkey + "&subkeys=";
    for (const param in itemParams) {
      url += param + ":" + itemParams[param] + ";";
    }

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseBillServiceScan(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertBill(act: string, acttype: IceTypes.ACCOUNT_TYPE, bill: superbillcharges): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (bill == null) throw "Bill object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=insertbill&domaintype=" + acttype.toString() + "&key=" + bill.superbill_charges_key + "&subkeys=";

    let tblname = bill.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(bill) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseBill(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public updateBill(act: string, acttype: IceTypes.ACCOUNT_TYPE, bill: superbillcharges): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (bill == null) throw "Bill object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=updatebill&domaintype=" + acttype.toString() + "&key=" + bill.superbill_charges_key + "&subkeys=";

    let tblname = bill.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(bill) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseBill(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public deleteBill(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Key value invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=deletebill&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseBill(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getBillTemplates(act: string, acttype: IceTypes.ACCOUNT_TYPE, billtoacttype: IceTypes.ACCOUNT_TYPE, taxonomy: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=billtemplates&domaintype=" + acttype.toString() + "&key=&subkeys=" + "billtoacttype:" + billtoacttype.toString() + ";taxonomy:" + taxonomy + ";";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseBillTemplateIndex(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getBillTemplate(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=billtemplate&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseBillTemplate(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public addBillTemplate(act: string, acttype: IceTypes.ACCOUNT_TYPE, template: superbill): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (template == null) throw "Template object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=addbilltemplate&domaintype=" + acttype.toString() + "&key=" + template.superbill_key + "&subkeys=";

    let tblname = template.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(template) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseBillTemplate(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public updateBillTemplate(act: string, acttype: IceTypes.ACCOUNT_TYPE, template: superbill): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (template == null) throw "Template object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=updatebilltemplate&domaintype=" + acttype.toString() + "&key=" + template.superbill_key + "&subkeys=";

    let tblname = template.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(template) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseBillTemplate(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public processRecurringCharges(act: string, acttype: IceTypes.ACCOUNT_TYPE, bill: superbillcharges,
    frequency: IceTypes.recurringtype,
    frequencyVal: number,
    startDate: string,
    svcs: medicalservice[]): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (bill == null) throw "Bill object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=processrecurringcharges&domaintype=" + acttype.toString() + "&key=&subkeys=" + "freq|" + frequency.toString() + ";" + "freqval|" + frequencyVal.toString() + ";" + "start|" + startDate;

    let billTblname = bill.gettablename().toLowerCase();
    let alldata = ["{" + JSON.stringify(billTblname) + ": " + JSON.stringify(bill) + "}"];

    if (svcs.length > 0) {
      let tblname = svcs[0].gettablename().toLowerCase();
      for (let ichrg = 0; ichrg < svcs.length; ichrg++) {
        let charge = svcs[ichrg];
        let chgdata = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(charge) + "}";
        alldata.push(chgdata);
      }
    }
    let data = '[' + alldata.join() + ']';

    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseBill(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }


  public getPayment(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=payment&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parsePayment(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPayments(act: string, actType: IceTypes.ACCOUNT_TYPE, keys: string[]): Observable<any> {
    if (keys.length === 0)
      return of(null);

    let promises = [];
    for (let iKey = 0; iKey < keys.length; iKey++) {
      promises.push(this.getPayment(act, actType, keys[iKey]));
    }

    return forkJoin(promises);
  }

  ///////////////////////////////
  // Journal
  public getJournalSummary(act: string, acttype: IceTypes.ACCOUNT_TYPE, from: string, to: string, journal: string, coa: IceTypes.coa_types, reportkey: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=journalsummary&domaintype=" + acttype.toString() + "&key=&subkeys=" + "journal|" + journal + ";" + "coa_type|" + coa.toString() + ";from|" + from + ";to|" + to + ";rptkey|" + reportkey;
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.geticexdocumentobjects(response, ["REPORT_DATA", "REPORT"])
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, from: string, to: string, journal: string, coa: IceTypes.coa_types, serviceKey: string = "", coaEndRange: IceTypes.coa_types = IceTypes.coa_types.coaCashSecondary, perKey: string = null): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=journal&domaintype=" + acttype.toString() + "&key=&subkeys=" + "journal:" + journal + ";" + "coa_type:" + coa.toString() + ";" + "service_key:" + serviceKey + ";" + "coa_type_end_range:" + coaEndRange.toString();
    if (perKey != null)
      url += ";" + "period_key:" + perKey.toString();

    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getJournals(act: string, acttype: IceTypes.ACCOUNT_TYPE, from: string, to: string, journal: string, coa: IceTypes.coa_types, serviceKeys: string[] = [], coaEndRange: IceTypes.coa_types = IceTypes.coa_types.coaCashSecondary, perKey: string = null): Observable<any> {
    if (serviceKeys.length === 0)
      return of(null);

    let promises = [];
    for (let iKey = 0; iKey < serviceKeys.length; iKey++) {
      let key = serviceKeys[iKey];

      promises.push(this.getJournal(act, acttype, from, to, journal, coa, key, coaEndRange, perKey));
    }

    return forkJoin(promises);
  }

  public insertUpdateAssetJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, amount: number, increase: boolean, jrnl: assetjournal): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (jrnl == null) throw "Journal object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=insertupdateassetjournal&domaintype=" + acttype.toString() + "&key=" + jrnl.asset_journal_key + "&subkeys=" + "amount:" + amount.toString() + ";increase:" + increase.toString();

    let tblname = jrnl.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(jrnl) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertUpdateExpenseJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, amount: number, increase: boolean, jrnl: expensejournal): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (jrnl == null) throw "Journal object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=insertupdateexpensejournal&domaintype=" + acttype.toString() + "&key=" + jrnl.expense_journal_key + "&subkeys=" + "amount:" + amount.toString() + ";increase:" + increase.toString();

    let tblname = jrnl.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(jrnl) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertUpdateIncomeJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, amount: number, increase: boolean, jrnl: incomerevenuejournal): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (jrnl == null) throw "Journal object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=insertupdateincomejournal&domaintype=" + acttype.toString() + "&key=" + jrnl.income_revenue_journal_key + "&subkeys=" + "amount:" + amount.toString() + ";increase:" + increase.toString();

    let tblname = jrnl.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(jrnl) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertUpdateLiabilityJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, amount: number, increase: boolean, jrnl: liabilityjournal): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (jrnl == null) throw "Journal object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=insertupdateliabilityjournal&domaintype=" + acttype.toString() + "&key=" + jrnl.liability_journal_key + "&subkeys=" + "amount:" + amount.toString() + ";increase:" + increase.toString();

    let tblname = jrnl.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(jrnl) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertUpdateOtherItemsJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, amount: number, increase: boolean, jrnl: otheritemsjournal): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (jrnl == null) throw "Journal object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=insertupdateotheritemsjournal&domaintype=" + acttype.toString() + "&key=" + jrnl.other_items_journal_key + "&subkeys=" + "amount:" + amount.toString() + ";increase:" + increase.toString();

    let tblname = jrnl.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(jrnl) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public moveJournals(act: string, acttype: IceTypes.ACCOUNT_TYPE, tblname: string, jrnls: any[], fromPeriodKey, toPeriodKey: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (jrnls == null || jrnls.length == 0) throw "Journals object invalid.";
    if (tblname == null || tblname.length == 0) throw "Table name input invalid.";
    if (fromPeriodKey == null) throw "Move from Period input invalid."; // from period can be empty
    if (toPeriodKey == null) throw "Move to Period input invalid."; // to period can be empty
    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=movejournals&domaintype=" + acttype.toString() + "&key=" + toPeriodKey + "&subkeys=" + "tablename:" + tblname + ";fromperiod:" + fromPeriodKey;

    let data = "{" + JSON.stringify(tblname.toLowerCase()) + ": " + JSON.stringify(jrnls) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public deleteAssetJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Journal object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=deleteassetjournal&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public deleteIncomeJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Journal object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=deleteincomejournal&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public deleteExpenseJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Journal object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=deleteexpensejournal&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public deleteInventoryJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Journal object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=deleteinventoryjournal&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public deleteLiabilityJournal(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Journal object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=deleteliabilityjournal&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseJournals(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertServices(act: string, acttype: IceTypes.ACCOUNT_TYPE, svc: medicalservice): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=insertservices&domaintype=" + acttype.toString() + "&key=&subkeys=extkey:;srctype:";
    let tblname = svc.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(svc) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["MEDICAL_SERVICE"]));
  }

  public updateServices(act: string, acttype: IceTypes.ACCOUNT_TYPE, svc: medicalservice[]): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=updateservices&domaintype=" + acttype.toString() + "&key=&subkeys=extkey:;srctype:";
    if (svc.length == 0) throw "Service value is required.";
    let tblname = svc[0].gettablename().toLowerCase();
    let alldata = [];
    for (let ichrg = 0; ichrg < svc.length; ichrg++) {
      let charge = svc[ichrg];
      let chgdata = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(charge) + "}";
      alldata.push(chgdata);
    }
    let data = '[' + alldata.join(",") + ']';
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["MEDICAL_SERVICE"]));
  }

  public getCurrentPeriod(act: string, acttype: IceTypes.ACCOUNT_TYPE): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=currentperiod&domaintype=" + acttype.toString() + "&key=" + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parsePeriod(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPeriod(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=period&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parsePeriod(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPeriods(act: string, acttype: IceTypes.ACCOUNT_TYPE): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=periods&domaintype=" + acttype.toString() + "&key=&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parsePeriodIndex(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertUpdatePeriod(act: string, acttype: IceTypes.ACCOUNT_TYPE, setascurrect: boolean, per: accountingperiod): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (per == null) throw "Period object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=insertupdateperiod&domaintype=" + acttype.toString() + "&key=" + per.accounting_period_key + "&subkeys=setascurrent:" + (setascurrect ? "true" : "false") + ";";

    let tblname = per.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(per) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parsePeriod(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  // Payment Operations
  public postPayment(act: string, acttype: IceTypes.ACCOUNT_TYPE, pay: payment): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=postpayment&domaintype=" + acttype.toString() + "&key=&subkeys=extkey:;srctype:";
    let tblname = pay.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(pay) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["PAYMENT"]));
  }

  public postOneChargePayment(act: string, acttype: IceTypes.ACCOUNT_TYPE, pay: payment): Observable<any> {
    if (act == null || act.length == 0 || pay == null) return Observable.throw("Error: Invalid Parameter");

    let ent: entity = pay.ENTS != null && pay.ENTS.length > 0 ? pay.ENTS[0] : null;
    if (ent == null) {
      ent = new entity();
      ent.entity_key = Iceutility.guid(true);
      pay.ENTS.push(ent);
    }

    // To do change this to pass in the of the person/org making the payment
    pay.billed_to = ""
    if (pay.account_type == IceTypes.ACCOUNT_TYPE.actPatient || pay.account_type == IceTypes.ACCOUNT_TYPE.actSubscriber) {
      let first = "Unknown", last = "Unknown";
      pay.entity_key = ent.entity_key;
      ent.first_name = first;
      ent.last_name = last;
      ent.entity_identifier_code = IceTypes.entity_type_qualifier.etqPerson;
    }
    else {
      pay.entity_key = ent.entity_key;
      ent.organization_name = "Unknown";
      ent.entity_identifier_code = IceTypes.entity_type_qualifier.etqNonPerson;
    }

    return this.postPayment(act, acttype, pay);
  }

  public processBillPayment(act: string, acttype: IceTypes.ACCOUNT_TYPE, ord: orderobject): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=processbillpayment&domaintype=" + acttype.toString() + "&key=&subkeys=extkey:;srctype:";
    let tblname = ord.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(ord) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["ORDER"]));
  }

  public getClaims(act: string, acttype: IceTypes.ACCOUNT_TYPE, dom: string, domType: IceTypes.ACCOUNT_TYPE, fromDt: string, toDt: string, keywd: string, code: string, status: IceTypes.trans_status, medsvcKey?: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=claimhistory&domaintype=" + acttype.toString() + "&key=&subkeys=" + "fordomain|" + dom + ";" + "fordomaintype|" + domType.toString() + ";" + "from|" + fromDt + ";" + "to|" + toDt + ";" + "keyword|" + keywd + ";" + "code|" + code + ";" + "status|" + status + ";";
    if (medsvcKey != null) {
      url += "servicekey|" + medsvcKey + ";";
    }
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimIndex(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getClaimBuckets(act: string, acttype: IceTypes.ACCOUNT_TYPE, billto: string,
    domType?: IceTypes.ACCOUNT_TYPE, fromDt?: string, toDt?: string, dateField?: string, status?: IceTypes.trans_status): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=claimbuckethistory&domaintype=" + acttype.toString() + "&key=&subkeys=" + "fordomain|" + billto + ";";
    if (domType != null) url += "fordomaintype|" + domType.toString() + ";";
    if (fromDt != null) url += "from|" + fromDt.toString() + ";";
    if (toDt != null) url += "to|" + toDt.toString() + ";";
    if (dateField != null) url += "datefield|" + dateField.toString() + ";";
    if (status != null) url += "status|" + status.toString() + ";";

    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimBucketIndex(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getClaimBucket(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=claimbucket&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimBucket(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getClaimBucketStatus(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=claimbucketstatus&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimBucketIndex(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getClaimBucketsByBill(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=claimbucketsbybill&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimBucket(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertClaim(act: string, acttype: IceTypes.ACCOUNT_TYPE, clm: claimbucket): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (clm == null) throw "Claim Bucket object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=addclaimbucket&domaintype=" + acttype.toString() + "&key=" + clm.claim_bucket_key + "&subkeys=";

    let tblname = clm.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(clm) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimBucket(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public updateClaim(act: string, acttype: IceTypes.ACCOUNT_TYPE, clm: claimbucket): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (clm == null) throw "Claim Bucket object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=updateclaimbucket&domaintype=" + acttype.toString() + "&key=" + clm.claim_bucket_key + "&subkeys=";

    let tblname = clm.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(clm) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimBucket(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public deleteClaim(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Key value invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=deleteclaimbucket&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimBucket(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public deleteClaimItem(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string, clmkey: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Key value invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=deleteclaimitem&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=" + "clmkey:" + clmkey;

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimBucket(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public cloneClaimItem(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string, clmkey: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Key value invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=cloneclaimitem&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=" + "clmkey:" + clmkey;

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaim(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public cloneClaimBucket(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Key value invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=cloneclaimbucket&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimBucket(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public insertUpdateClaimBuckets(act: string, acttype: IceTypes.ACCOUNT_TYPE, buckets: claimbucket[]): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=insertupdatebuckets&domaintype=" + acttype.toString() + "&key=&subkeys=extkey:;srctype:";
    if (buckets.length == 0) throw "Buckets value is required.";
    let tblname = buckets[0].gettablename().toLowerCase();
    let alldata = [];
    for (let ichrg = 0; ichrg < buckets.length; ichrg++) {
      let charge = buckets[ichrg];
      let chgdata = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(charge) + "}";
      alldata.push(chgdata);
    }
    let data = '[' + alldata.join() + ']';
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["CLAIM_BUCKET"]));
  }

  public refreshClaimBuckets(act: string, acttype: IceTypes.ACCOUNT_TYPE, buckets: claimbucket[], clmkey: string, newsvcs: medicalservice[]): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Domain service is not configured.";
    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=refreshclaimbucket&domaintype=" + acttype.toString() + "&key=&subkeys=clmkey:" + clmkey;
    if (buckets.length == 0) throw "Buckets value is required.";
    let tblname = buckets[0].gettablename().toLowerCase();
    let alldata = [];
    for (let ibuck = 0; ibuck < buckets.length; ibuck++) {
      let bucket = buckets[ibuck];
      let buckdata = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(bucket) + "}";
      alldata.push(buckdata);
    }

    if (newsvcs != null) {
      for (let ichrg = 0; ichrg < newsvcs.length; ichrg++) {
        let charge = newsvcs[ichrg];
        let chgdata = "{" + JSON.stringify(charge.gettablename().toLowerCase()) + ": " + JSON.stringify(charge) + "}";
        alldata.push(chgdata);
      }
    }

    let data = '[' + alldata.join() + ']';
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error)
      )).map(response => this.geticexdocumentobjects(response, ["CLAIM_BUCKET"]));
  }

  public sendClaimBucket(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (key == null || key.length == 0) throw "Key value invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=sendclaimbucket&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";

    let data = "{}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseClaimBucket(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  //////////////////////////////
  // Accounts Payables
  public getPayToHistory(act: string, acttype: IceTypes.ACCOUNT_TYPE, billfrom: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=paytohistory&domaintype=" + acttype.toString() + "&key=&subkeys=" + "billto:" + billfrom + ";";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parsePayToIndex(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getPayTo(act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Accounting service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";

    let url = envs[currentEnv].icemedx_api + "/accounting/default.aspx?domain=" + act + "&data=payto&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parsePayTo(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }


  // Helpers
  static updateChargeHistoryPostingData(accSvc: AccountingService, act: string, acttype: IceTypes.ACCOUNT_TYPE, chgHist: chargehistory, svc: any, data: assetjournal[]): number {
    if (accSvc == null) return 0;
    if (chgHist == null) return 0;
    if (svc == null) return 0;
    if (data == null) return 0;

    chgHist.setServiceAssets(svc["medical_service_key"], data);
    chgHist.setAllServicePostings(svc["medical_service_key"], {});
    let credit = 0.0;
    for (let ijrnl = 0; ijrnl < data.length; ijrnl++) {
      let jrnl: assetjournal = data[ijrnl];
      let coatyp = Iceutility.toLong(jrnl.coa_type)
      if (coatyp == IceTypes.coa_types.coaAR
        || coatyp == IceTypes.coa_types.coaCoPayAR
        || coatyp == IceTypes.coa_types.coaCoInsuranceAR
        || coatyp == IceTypes.coa_types.coaDeductibleAR) {
        credit += Iceutility.toDouble(jrnl.credit);

        let postingData = chgHist.getServicePosting(svc["medical_service_key"], jrnl.src_key);
        let srctype = Iceutility.toLong(jrnl.src_type);
        if (srctype == IceTypes.SourceType.srcPayment
          && postingData == null) {
          accSvc.getPayment(act, acttype, jrnl.src_key).subscribe(
            paydata => {
              if (paydata != null && paydata.length > 0) {
                if (postingData == null) {
                  chgHist.setServicePosting(svc["medical_service_key"], jrnl.src_key, paydata[0]);
                }
              }
            },
            payerror => {
              console.log(payerror);
            });
        }
      }
    }
    return credit;
  }

  static setServiceSubscriberFromDDLKey(ctrl: IcecontrolSettings, charge: medicalservice): void {
    if (ctrl == null || charge == null) return;

    let key: string = ctrl.getFormControl().value;
    if (key == null || typeof key !== "string") {
      charge.subscriber_key = "";
      charge.coverage_key = "";
      charge.payer_key = "";
      return;
    }

    if (key.indexOf("|") >= 0) {
      let parts = key.split("|");
      charge.subscriber_key = parts[0];
      charge.coverage_key = parts[1];
      charge.payer_key = parts[2];
    }
    else {
      charge.subscriber_key = ctrl.getFormControl().value;
      charge.coverage_key = "";
      charge.payer_key = "";
    }
  }
  static setPaymentSubscriberFromDDLKey(ctrl: IcecontrolSettings, svcRef: servicepaymentreference): void {
    if (ctrl == null || svcRef == null) return;

    let key: string = ctrl.getFormControl().value;
    if (key == null || typeof key !== "string") {
      svcRef.subscriber_key = "";
      svcRef.coverage_key = "";
      svcRef.payer_key = "";
      return;
    }

    if (key.indexOf("|") >= 0) {
      let parts = key.split("|");
      svcRef.subscriber_key = parts[0];
      svcRef.coverage_key = parts[1];
      svcRef.payer_key = parts[2];
    }
    else {
      svcRef.subscriber_key = ctrl.getFormControl().value;
      svcRef.coverage_key = "";
      svcRef.payer_key = "";
    }
  }

  static setSubscriberReferenceFromDDLKey(ctrl: IcecontrolSettings, subRef: subscriberreference): void {
    if (ctrl == null || subRef == null) return;

    let key: string = ctrl.getFormControl().value;
    if (key == null || typeof key !== "string") {
      subRef.subscriber_key = "";
      subRef.coverage_key = "";
      subRef.payer_key = "";
      return;
    }

    if (key.indexOf("|") >= 0) {
      let parts = key.split("|");
      subRef.subscriber_key = parts[0];
      subRef.coverage_key = parts[1];
      subRef.payer_key = parts[2];
    }
    else {
      subRef.subscriber_key = ctrl.getFormControl().value;
      subRef.coverage_key = "";
      subRef.payer_key = "";
    }
  }

  static setSubscriberAmountFromDDLKey(ctrl: IcecontrolSettings, amtRef: amount): void {
    if (ctrl == null || amtRef == null) return;

    let key: string = ctrl.getFormControl().value;
    if (key == null || typeof key !== "string") {
      amtRef.subscriber_key = "";
      amtRef.coverage_key = "";
      amtRef.payer_key = "";
      return;
    }

    if (key.indexOf("|") >= 0) {
      let parts = key.split("|");
      amtRef.subscriber_key = parts[0];
      amtRef.coverage_key = parts[1];
      amtRef.payer_key = parts[2];
    }
    else {
      amtRef.subscriber_key = ctrl.getFormControl().value;
      amtRef.coverage_key = "";
      amtRef.payer_key = "";
    }
  }

  static getSourceType(stype: IceTypes.SourceType): string {
    switch (stype) {
      case IceTypes.SourceType.srcDiagnosis:
        return "Diagnosis";
      case IceTypes.SourceType.srcTypeOfService:
        return "Type Of Service";
      case IceTypes.SourceType.srcTestProcedure:
        return "Diagnostic Tests";
      case IceTypes.SourceType.srcClinicalResults:
        return "Clinical Results";
      case IceTypes.SourceType.srcCare:
        return "Inpatient Admission/Discharge";
      case IceTypes.SourceType.srcCareDetail:
        return "Inpatient Service";
      case IceTypes.SourceType.srcProcedure:
        return "Procedure/Surgery";
      case IceTypes.SourceType.srcMedication:
        return "Medication";
      case IceTypes.SourceType.srcReferral:
        return "Referral";
      case IceTypes.SourceType.srcFamilyConference:
        return "Family Conference";
      case IceTypes.SourceType.srcOther:
        return "Other Supplies";
      case IceTypes.SourceType.srcService:
        return "Outpatient Service";
      case IceTypes.SourceType.srcEncounterNotes:
        return "Encounter Progress";

      case IceTypes.SourceType.srcStatement:
        return "Statement";
      case IceTypes.SourceType.srcPayment:
        return "Payment";
      case IceTypes.SourceType.srcCoPay:
        return "Co-Pay";
      case IceTypes.SourceType.srcERA:
        return "Electronic Remittance";

      case IceTypes.SourceType.srcXRayRad:
        return "X ray(s)";
      case IceTypes.SourceType.srcPrescriptionRefill:
        return "Prescription Refill";
      case IceTypes.SourceType.srcExam:
        return "Exam";
      case IceTypes.SourceType.srcImmunization:
        return "Immunization";
      case IceTypes.SourceType.srcToothService:
        return "Dental Service";
      case IceTypes.SourceType.srcToothPlan:
        return "Dental Plan";
      case IceTypes.SourceType.srcPatientDemographics:
        return "Patient Demographics";
      case IceTypes.SourceType.srcMiscellaneousForms:
        return "Miscellaneous";
      case IceTypes.SourceType.srcImage:
        return "Image";
      case IceTypes.SourceType.srcTreatmentPlan:
        return "Treatment Plan";
      case IceTypes.SourceType.srcPatientPhoto:
        return "Patient Photo";
      case IceTypes.SourceType.srcCCD:
        return "Continuity Care Document";
      case IceTypes.SourceType.srcAllergies:
        return "Allergies";
      case IceTypes.SourceType.srcVitals:
        return "Vitals";
      case IceTypes.SourceType.srcInfomatics:
        return "Infomatics and Reporting";
    }
    return "Miscellaneous";
  }

  static getPartyBucketKey(charge: medicalservice): string {
    if (charge == null) return "";
    let key = charge.subscriber_key == null ? "" : charge.subscriber_key;
    if (key.length > 0) {
      key += "|" + (charge.coverage_key == null ? "" : charge.coverage_key);
      key += "|" + (charge.payer_key == null ? "" : charge.payer_key);
    }
    return key;
  }

  static getPartyBucketKeyEx(subKey: string, covKey: string, payKey: string): string {
    let key = subKey == null ? "" : subKey;
    if (key.length > 0) {
      key += "|" + (covKey == null ? "" : covKey);
      key += "|" + (payKey == null ? "" : payKey);
    }
    return key;
  }

  static getResponsiblePartyShortName(name: string): string {
    if (name == null || name.length == 0) return "";

    let parts = name.split(" ");
    let shortname = "";
    for (let i = 0; i < parts.length; i++) {
      let part: string = parts[i];
      if (part.length == 0 || part.substring(0, 1) == "(") continue;
      shortname += part.substring(0, 1);
    }
    return shortname;
  }

  static getUnitTypes(): any[] {
    var types = [];
    try {
      types.push({ value: IceTypes.unit_type.utypeUnknown.toString(), name: AccountingService.getUnitType(IceTypes.unit_type.utypeUnknown) });
      types.push({ value: IceTypes.unit_type.utypeEach.toString(), name: AccountingService.getUnitType(IceTypes.unit_type.utypeEach) });
      types.push({ value: IceTypes.unit_type.utypeBox.toString(), name: AccountingService.getUnitType(IceTypes.unit_type.utypeBox) });
      types.push({ value: IceTypes.unit_type.utypePackage.toString(), name: AccountingService.getUnitType(IceTypes.unit_type.utypePackage) });
      types.push({ value: IceTypes.unit_type.utypeRoll.toString(), name: AccountingService.getUnitType(IceTypes.unit_type.utypeRoll) });
    } catch (e) {
      console.log(e);
    }
    return types;
  }

  static getUnitType(type: any): string {
    switch (<IceTypes.unit_type>Iceutility.toLong(type)) {
      case IceTypes.unit_type.utypeEach:
        return "Each";
      case IceTypes.unit_type.utypeBox:
        return "Box";
      case IceTypes.unit_type.utypePackage:
        return "Package";
      case IceTypes.unit_type.utypeRoll:
        return "Roll";
    }
    return "Unknown";
  }

  static getInventoryMethods(): any[] {
    var types = [];
    try {
      types.push({ value: IceTypes.InventoryMethod.imFIFO.toString(), name: AccountingService.getInventoryMethod(IceTypes.InventoryMethod.imFIFO) });
      types.push({ value: IceTypes.InventoryMethod.imLIFO.toString(), name: AccountingService.getInventoryMethod(IceTypes.InventoryMethod.imLIFO) });
      types.push({ value: IceTypes.InventoryMethod.imWeightedAverage.toString(), name: AccountingService.getInventoryMethod(IceTypes.InventoryMethod.imWeightedAverage) });
    } catch (e) {
      console.log(e);
    }
    return types;
  }

  static getInventoryMethod(type: any): string {
    switch (<IceTypes.InventoryMethod>Iceutility.toLong(type)) {
      case IceTypes.InventoryMethod.imLIFO:
        return "Last-in / First-out Method";
      case IceTypes.InventoryMethod.imWeightedAverage:
        return "Weighted Average";
    }
    return "First-in / First-out Method";
  }

  static getJournalTypeList(): any[] {
    var types = [];
    try {
      types.push({ value: IceTypes.JournalType.jtAll.toString(), name: this.getJournalType(IceTypes.JournalType.jtAll) });
      types.push({ value: IceTypes.JournalType.jtAsset.toString(), name: this.getJournalType(IceTypes.JournalType.jtAsset) });
      types.push({ value: IceTypes.JournalType.jtExpense.toString(), name: this.getJournalType(IceTypes.JournalType.jtExpense) });
      types.push({ value: IceTypes.JournalType.jtIncome.toString(), name: this.getJournalType(IceTypes.JournalType.jtIncome) });
      types.push({ value: IceTypes.JournalType.jtLiability.toString(), name: this.getJournalType(IceTypes.JournalType.jtLiability) });
      types.push({ value: IceTypes.JournalType.jtInventory.toString(), name: this.getJournalType(IceTypes.JournalType.jtInventory) });
    } catch (e) {
      console.log(e);
    }
    return types;
  }

  static getJournalType(type: IceTypes.JournalType): string {
    switch (type) {
      case IceTypes.JournalType.jtAsset:
        return "Asset";
      case IceTypes.JournalType.jtExpense:
        return "Expense";
      case IceTypes.JournalType.jtIncome:
        return "Income";
      case IceTypes.JournalType.jtLiability:
        return "Liability";
      case IceTypes.JournalType.jtInventory:
        return "Inventory";
    }
    return "All Journals";
  }

  static getCoaTypeList(): any[] {
    var types = [];
    try {
      types.push({ value: IceTypes.coa_types.coaAsset.toString(), name: this.getCoaType(IceTypes.coa_types.coaAsset) });
      types.push({ value: IceTypes.coa_types.coaCashPrimary.toString(), name: this.getCoaType(IceTypes.coa_types.coaCashPrimary) });
      types.push({ value: IceTypes.coa_types.coaCashSecondary.toString(), name: this.getCoaType(IceTypes.coa_types.coaCashSecondary) });
      types.push({ value: IceTypes.coa_types.coaCashPetty.toString(), name: this.getCoaType(IceTypes.coa_types.coaCashPetty) });
      types.push({ value: IceTypes.coa_types.coaAR.toString(), name: this.getCoaType(IceTypes.coa_types.coaAR) });
      types.push({ value: IceTypes.coa_types.coaCoPayAR.toString(), name: this.getCoaType(IceTypes.coa_types.coaCoPayAR) });
      types.push({ value: IceTypes.coa_types.coaCoInsuranceAR.toString(), name: this.getCoaType(IceTypes.coa_types.coaCoInsuranceAR) });
      types.push({ value: IceTypes.coa_types.coaDeductibleAR.toString(), name: this.getCoaType(IceTypes.coa_types.coaDeductibleAR) });
      types.push({ value: IceTypes.coa_types.coaSupplies.toString(), name: this.getCoaType(IceTypes.coa_types.coaSupplies) });
      types.push({ value: IceTypes.coa_types.coaOfficeEquipment.toString(), name: this.getCoaType(IceTypes.coa_types.coaOfficeEquipment) });
      types.push({ value: IceTypes.coa_types.coaInventory.toString(), name: this.getCoaType(IceTypes.coa_types.coaInventory) });

      types.push({ value: IceTypes.coa_types.coaRevenue.toString(), name: this.getCoaType(IceTypes.coa_types.coaRevenue) });
      types.push({ value: IceTypes.coa_types.coaCoPayIncome.toString(), name: this.getCoaType(IceTypes.coa_types.coaCoPayIncome) });
      types.push({ value: IceTypes.coa_types.coaDeductibleIncome.toString(), name: this.getCoaType(IceTypes.coa_types.coaDeductibleIncome) });
      types.push({ value: IceTypes.coa_types.coaServiceIncome.toString(), name: this.getCoaType(IceTypes.coa_types.coaServiceIncome) });
      types.push({ value: IceTypes.coa_types.coaOtherIncome.toString(), name: this.getCoaType(IceTypes.coa_types.coaOtherIncome) });
      types.push({ value: IceTypes.coa_types.coaCoInsuranceIncome.toString(), name: this.getCoaType(IceTypes.coa_types.coaCoInsuranceIncome) });

      types.push({ value: IceTypes.coa_types.coaExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaExpense) });
      types.push({ value: IceTypes.coa_types.coaCostofSuppliesExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaCostofSuppliesExpense) });
      types.push({ value: IceTypes.coa_types.coaRentExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaRentExpense) });
      types.push({ value: IceTypes.coa_types.coaUtilitiesExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaUtilitiesExpense) });
      types.push({ value: IceTypes.coa_types.coaMiscellaneousExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaMiscellaneousExpense) });
      types.push({ value: IceTypes.coa_types.coaCostofServiceExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaCostofServiceExpense) });
      types.push({ value: IceTypes.coa_types.coaCostofInventoryExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaCostofInventoryExpense) });

      types.push({ value: IceTypes.coa_types.coaDiscount.toString(), name: this.getCoaType(IceTypes.coa_types.coaDiscount) });
      types.push({ value: IceTypes.coa_types.coaClaimAdjustment.toString(), name: this.getCoaType(IceTypes.coa_types.coaClaimAdjustment) });
      types.push({ value: IceTypes.coa_types.coaDisallowedAdjustment.toString(), name: this.getCoaType(IceTypes.coa_types.coaDisallowedAdjustment) });
      types.push({ value: IceTypes.coa_types.coaContractualObligationsAdjustment.toString(), name: this.getCoaType(IceTypes.coa_types.coaContractualObligationsAdjustment) });
      types.push({ value: IceTypes.coa_types.coaCorrectionandReversalsAdjustment.toString(), name: this.getCoaType(IceTypes.coa_types.coaCorrectionandReversalsAdjustment) });

      types.push({ value: IceTypes.coa_types.coaSalesTaxExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaSalesTaxExpense) });
      types.push({ value: IceTypes.coa_types.coaIncomeTaxExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaIncomeTaxExpense) });
      types.push({ value: IceTypes.coa_types.coaPayrollTaxExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaPayrollTaxExpense) });

      types.push({ value: IceTypes.coa_types.coaSalaryExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaSalaryExpense) });
      types.push({ value: IceTypes.coa_types.coaCommissionExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaCommissionExpense) });
      types.push({ value: IceTypes.coa_types.coaVacationExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaVacationExpense) });
      types.push({ value: IceTypes.coa_types.coaSalaryReimbursementExpense.toString(), name: this.getCoaType(IceTypes.coa_types.coaSalaryReimbursementExpense) });

      types.push({ value: IceTypes.coa_types.coaLiabilities.toString(), name: this.getCoaType(IceTypes.coa_types.coaLiabilities) });
      types.push({ value: IceTypes.coa_types.coaAP.toString(), name: this.getCoaType(IceTypes.coa_types.coaAP) });
      types.push({ value: IceTypes.coa_types.coaNotesPayable.toString(), name: this.getCoaType(IceTypes.coa_types.coaNotesPayable) });

      types.push({ value: IceTypes.coa_types.coaSalesTaxPayable.toString(), name: this.getCoaType(IceTypes.coa_types.coaSalesTaxPayable) });
      types.push({ value: IceTypes.coa_types.coaIncomeTaxPayable.toString(), name: this.getCoaType(IceTypes.coa_types.coaIncomeTaxPayable) });
      types.push({ value: IceTypes.coa_types.coaPayrollTaxPayable.toString(), name: this.getCoaType(IceTypes.coa_types.coaPayrollTaxPayable) });

      types.push({ value: IceTypes.coa_types.coaSalaryPayable.toString(), name: this.getCoaType(IceTypes.coa_types.coaSalaryPayable) });
      types.push({ value: IceTypes.coa_types.coaCommissionPayable.toString(), name: this.getCoaType(IceTypes.coa_types.coaCommissionPayable) });
      types.push({ value: IceTypes.coa_types.coaVacationPayable.toString(), name: this.getCoaType(IceTypes.coa_types.coaVacationPayable) });
      types.push({ value: IceTypes.coa_types.coaSalaryReimbursementPayable.toString(), name: this.getCoaType(IceTypes.coa_types.coaSalaryReimbursementPayable) });

      types.push({ value: IceTypes.coa_types.coaEquity.toString(), name: this.getCoaType(IceTypes.coa_types.coaEquity) });
      types.push({ value: IceTypes.coa_types.coaOwnersCapital.toString(), name: this.getCoaType(IceTypes.coa_types.coaOwnersCapital) });
      types.push({ value: IceTypes.coa_types.coaOwnersDrawing.toString(), name: this.getCoaType(IceTypes.coa_types.coaOwnersDrawing) });
    } catch (e) {
      console.log(e);
    }
    return types;
  }

  static getCoaType(ncoa_type: IceTypes.coa_types): string {
    switch (Iceutility.toLong(ncoa_type)) {
      case IceTypes.coa_types.coaAsset:
        return "Asset";
      case IceTypes.coa_types.coaCashPrimary:
        return "Primary Cash";
      case IceTypes.coa_types.coaCashSecondary:
        return "Secondary Cash";
      case IceTypes.coa_types.coaCashPetty:
        return "Petty Cash";
      case IceTypes.coa_types.coaAR:
        return "Accounts Receivable";
      case IceTypes.coa_types.coaCoPayAR:
        return "Co-Pay Receivable";
      case IceTypes.coa_types.coaCoInsuranceAR:
        return "Co-Insurance Receivable";
      case IceTypes.coa_types.coaDeductibleAR:
        return "Deductible Receivable";
      case IceTypes.coa_types.coaSupplies:
        return "Supplies";
      case IceTypes.coa_types.coaOfficeEquipment:
        return "Office Equipment";
      case IceTypes.coa_types.coaInventory:
        return "Inventory";

      case IceTypes.coa_types.coaRevenue:
        return "Revenue";
      case IceTypes.coa_types.coaCoPayIncome:
        return "Co-Pay";
      case IceTypes.coa_types.coaDeductibleIncome:
        return "Deductible";
      case IceTypes.coa_types.coaServiceIncome:
        return "Service";
      case IceTypes.coa_types.coaOtherIncome:
        return "Other Service or Supply";
      case IceTypes.coa_types.coaCoInsuranceIncome:
        return "Co-Insurance";

      case IceTypes.coa_types.coaExpense:
        return "Expense";
      case IceTypes.coa_types.coaCostofSuppliesExpense:
        return "Cost of Supplies Expense";
      case IceTypes.coa_types.coaRentExpense:
        return "Rent Expense";
      case IceTypes.coa_types.coaUtilitiesExpense:
        return "Utilities Expense";
      case IceTypes.coa_types.coaMiscellaneousExpense:
        return "Miscellaneous Expense";
      case IceTypes.coa_types.coaCostofServiceExpense:
        return "Cost of Service Expense";
      case IceTypes.coa_types.coaCostofInventoryExpense:
        return "Cost of Inventory Expense";

      case IceTypes.coa_types.coaDiscount:
        return "Discount";
      case IceTypes.coa_types.coaClaimAdjustment:
        return "Claim Adjustment";
      case IceTypes.coa_types.coaDisallowedAdjustment:
        return "Disallowed Adjustment";
      case IceTypes.coa_types.coaContractualObligationsAdjustment:
        return "Contractual Obligation Adjustment";
      case IceTypes.coa_types.coaCorrectionandReversalsAdjustment:
        return "Correction and Reversals Adjustment";

      case IceTypes.coa_types.coaSalesTaxExpense:
        return "Sales Tax Expense";
      case IceTypes.coa_types.coaIncomeTaxExpense:
        return "Income Tax Expense";
      case IceTypes.coa_types.coaPayrollTaxExpense:
        return "Payroll Tax Expense";

      case IceTypes.coa_types.coaSalaryExpense:
        return "Salary Expense";
      case IceTypes.coa_types.coaCommissionExpense:
        return "Commission Expense";
      case IceTypes.coa_types.coaVacationExpense:
        return "Vacation Expense";
      case IceTypes.coa_types.coaSalaryReimbursementExpense:
        return "Salary Reimbursement Expense";

      case IceTypes.coa_types.coaLiabilities:
        return "Liabilities";
      case IceTypes.coa_types.coaAP:
        return "Accounts Payable";
      case IceTypes.coa_types.coaNotesPayable:
        return "Notes Payable";

      case IceTypes.coa_types.coaSalesTaxPayable:
        return "Sales Tax Payable";
      case IceTypes.coa_types.coaIncomeTaxPayable:
        return "Income Tax Payable";
      case IceTypes.coa_types.coaPayrollTaxPayable:
        return "Payroll Tax Payable";

      case IceTypes.coa_types.coaSalaryPayable:
        return "Salary Payable";
      case IceTypes.coa_types.coaCommissionPayable:
        return "Commission Payable";
      case IceTypes.coa_types.coaVacationPayable:
        return "Vacation Payable";
      case IceTypes.coa_types.coaSalaryReimbursementPayable:
        return "Salary Reimbursement Payable";

      case IceTypes.coa_types.coaEquity:
        return "Equity";
      case IceTypes.coa_types.coaOwnersCapital:
        return "Owner's Capital Investment";
      case IceTypes.coa_types.coaOwnersDrawing:
        return "Owner's Drawing";
    }
    return "";
  }

  static getPeriodStatus(type: IceTypes.period_status): string {
    switch (type) {
      case IceTypes.period_status.psCreated:
      case IceTypes.period_status.psStarted:
        return "Open";
      case IceTypes.period_status.psFinished:
        return "Closed";
      case IceTypes.period_status.psPaused:
        return "Paused";
    }
    return "Unknown";
  }

  static getTaxStatus(type: IceTypes.sales_tax_status) {
    switch (Iceutility.toLong(type)) {
      case IceTypes.sales_tax_status.stsNonTaxable:
        return "Non-Taxable";
      case IceTypes.sales_tax_status.stsTaxable:
        return "Taxable";
      default:
        return "";
    }
  }

  static getPeriodDesc(actPer: accountingperiod, statusDesc?: string): string {
    if (actPer == null) return "";
    let periodDesc = actPer.title + " - ";
    if (actPer.start_date != null && actPer.start_date.length > 0)
      periodDesc += "From " + (Iceutility.isTextDateTime(actPer.start_date) ? Iceutility.getDateTextEx(actPer.start_date, null, false) : Iceutility.getDateTextEx(Iceutility.ConvertFromDotNetServerTime(actPer.start_date), null, false));

    if (actPer.end_date != null && actPer.end_date.length > 0)
      periodDesc += " To " + (Iceutility.isTextDateTime(actPer.end_date) ? Iceutility.getDateTextEx(actPer.end_date, null, false) : Iceutility.getDateTextEx(Iceutility.ConvertFromDotNetServerTime(actPer.end_date), null, false));

    if (statusDesc != null && statusDesc.length > 0)
      periodDesc += (" <" + statusDesc + ">");

    return periodDesc;
  }

  private parseItemReceiving(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["OTHER_ITEMS_RECEIVING"]);
  }

  private parseItemReceivingScan(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["OTHER_ITEMS_RECEIVING", "OTHER_ITEMS", "PURCHASE_DELIVERY"]);
  }

  private parseServiceScan(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["MEDICAL_SERVICE"]);
  }

  private parseBillServiceScan(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["SUPERBILL_CHARGES", "MEDICAL_SERVICE"]);
  }

  private parseOtherItemsIndex(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["OTHER_ITEMS_INDEX"]);
  }

  private parsePurchasesIndex(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["PURCHASE_INDEX"]);
  }

  private parsePurchaseDelivery(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["PURCHASE_DELIVERY"]);
  }

  private parseBillsIndex(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["SUPERBILL_CHARGES_INDEX"]);
  }

  private parseServiceIndex(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["MEDICAL_SERVICE_INDEX"]);
  }

  private parseBill(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["SUPERBILL_CHARGES", "MEDICAL_SERVICE"]);
  }

  private parseBillTemplateIndex(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["SUPERBILL_INDEX"]);
  }

  private parseBillTemplate(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["SUPERBILL"]);
  }

  private parseClaimBucket(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["SUPERBILL_CHARGES", "CLAIM_BUCKET", "MEDICAL_SERVICE"]);
  }

  private parseClaim(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["CLAIM"]);
  }

  private parsePeriod(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["ACCOUNTING_PERIOD"]);
  }

  private parsePeriodIndex(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["ACCOUNTING_PERIOD_INDEX"]);
  }

  private parseJournals(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["INCOME_REVENUE_JOURNAL", "ASSET_JOURNAL", "EXPENSE_JOURNAL", "LIABILITY_JOURNAL", "OTHER_ITEMS_JOURNAL"]);
  }

  private parseOtherItems(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["OTHER_ITEMS"]);
  }

  private parsePayToIndex(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["PAY_TO_INDEX"]);
  }

  private parsePayTo(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["PAY_TO", "MEDICAL_SERVICE"]);
  }

  private parsePayment(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["PAYMENT"]);
  }

  private parseClaimIndex(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["CLAIM_INDEX"]);
  }

  private parseClaimBucketIndex(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["CLAIM_BUCKET_INDEX"]);
  }
}
